import {reactive} from "vue";
import Cookie from "js-cookie";

/**
 * The cookie choice of the visitor. The banner and the settings come from vanilla-cookieconsent (loaded after the
 * page, in its own chunk); the choice is saved in its cookie. The inline script of the page (components/consent.blade.php)
 * reads the same cookie before any tag, sends the Google Consent Mode update and starts GTM, GA4, the Meta pixel and the
 * Maps API. This file is what the Vue side uses: the embeds (maps, video) and the Session Replay start only when
 * their category is allowed.
 *
 * Categories:  analytics (measurement, Session Replay), marketing (ads, Meta pixel), external (maps, videos).
 */
export type ConsentCategory = 'analytics' | 'marketing' | 'external';

export interface ConsentState {
	analytics: boolean,
	marketing: boolean,
	external: boolean,
	decided: boolean,
}

const COOKIE_NAME = (window as any).albaluxConsent?.config?.cookie ?? 'cc_cookie';
const COOKIE_DAYS = (window as any).albaluxConsent?.config?.days ?? 180;
const CATEGORIES: ConsentCategory[] = ['analytics', 'marketing', 'external'];

export const consent = reactive<ConsentState>({analytics: false, marketing: false, external: false, decided: false});

let cookieConsent: typeof import("vanilla-cookieconsent") | null = null;
let started = false;

const accepted = (): string[] | null => {
	const stored = (window as any).albaluxConsent?.get?.();
	if (!stored) {
		return null;
	}
	return CATEGORIES.filter((category) => stored[category]);
};

const sync = (categories: string[] | null): void => {
	consent.decided = categories !== null;
	CATEGORIES.forEach((category) => consent[category] = !!categories?.includes(category));
};

// The choice that was saved earlier is known at once, before the banner library is loaded
sync(accepted());

const translationsFor = (lang: 'hu' | 'en') => {
	const t = {
		hu: {
			title: 'Sütik és külső tartalmak',
			description: 'A működéshez szükséges sütiket mindig használjuk. A statisztikai és marketing mérést (Google Analytics, Google Ads, Meta), a munkamenet-rögzítést és a külső tartalmakat (Google Térkép, YouTube) csak a hozzájárulásoddal töltjük be. A döntésedet bármikor módosíthatod.',
			acceptAll: 'Összes elfogadása',
			acceptNecessary: 'Csak a szükségesek',
			showPreferences: 'Beállítások',
			prefTitle: 'Süti beállítások',
			save: 'Kiválasztottak mentése',
			close: 'Bezárás',
			sections: [
				{title: 'Használat', description: 'Itt döntheted el, mely sütiket és külső szolgáltatásokat engedélyezed. A „Csak a szükségesek” választással semmilyen mérés és külső tartalom nem indul el.'},
				{title: 'Szükséges', description: 'Az oldal működéséhez kellenek (például a nyelv és a döntésed megjegyzése). Nem kapcsolhatók ki.'},
				{title: 'Statisztika', description: 'Anonim látogatásmérés (Google Analytics) és a hibák megértését segítő munkamenet-rögzítés.'},
				{title: 'Marketing', description: 'Hirdetések mérése és személyre szabása (Google Ads, Meta).'},
				{title: 'Külső tartalmak', description: 'Beágyazott tartalmak, amelyek a szolgáltató (Google, YouTube) sütiket állíthatnak be: térképek és videók.'},
			],
		},
		en: {
			title: 'Cookies and external content',
			description: 'We always use the cookies that the site needs to work. Statistics and marketing measurement (Google Analytics, Google Ads, Meta), session recording and external content (Google Maps, YouTube) are loaded only with your consent. You can change your choice at any time.',
			acceptAll: 'Accept all',
			acceptNecessary: 'Necessary only',
			showPreferences: 'Settings',
			prefTitle: 'Cookie settings',
			save: 'Save selection',
			close: 'Close',
			sections: [
				{title: 'Usage', description: 'Here you decide which cookies and external services you allow. With "Necessary only" no measurement and no external content is started.'},
				{title: 'Necessary', description: 'Needed for the site to work (for example remembering the language and your choice). They cannot be switched off.'},
				{title: 'Statistics', description: 'Anonymous visit measurement (Google Analytics) and session recording that helps to understand errors.'},
				{title: 'Marketing', description: 'Ad measurement and personalisation (Google Ads, Meta).'},
				{title: 'External content', description: 'Embedded content whose provider (Google, YouTube) can set cookies: maps and videos.'},
			],
		},
	}[lang];

	return {
		consentModal: {
			title: t.title,
			description: t.description,
			acceptAllBtn: t.acceptAll,
			acceptNecessaryBtn: t.acceptNecessary,
			showPreferencesBtn: t.showPreferences,
		},
		preferencesModal: {
			title: t.prefTitle,
			acceptAllBtn: t.acceptAll,
			acceptNecessaryBtn: t.acceptNecessary,
			savePreferencesBtn: t.save,
			closeIconLabel: t.close,
			sections: [
				{title: t.sections[0].title, description: t.sections[0].description},
				{title: t.sections[1].title, description: t.sections[1].description, linkedCategory: 'necessary'},
				{title: t.sections[2].title, description: t.sections[2].description, linkedCategory: 'analytics'},
				{title: t.sections[3].title, description: t.sections[3].description, linkedCategory: 'marketing'},
				{title: t.sections[4].title, description: t.sections[4].description, linkedCategory: 'external'},
			],
		},
	};
};

/**
 * Applies the saved choice: Consent Mode update, tags, cookies. A withdrawn consent reloads the page, because a script
 * that was loaded (the tags, the Pixel, the map, the player, the Replay) cannot be unloaded.
 */
const choiceChanged = (categories: string[], withdrawn: boolean): void => {
	const wasAllowed = {analytics: consent.analytics, marketing: consent.marketing, external: consent.external};
	sync(categories);
	(window as any).albaluxConsent?.apply?.({analytics: consent.analytics, marketing: consent.marketing, external: consent.external});

	if (withdrawn && CATEGORIES.some((category) => wasAllowed[category] && !consent[category])) {
		window.location.reload();
	}
};

const addReopenButton = (translation: ReturnType<typeof translationsFor>): void => {
	if (document.querySelector('.consent-reopen')) {
		return;
	}
	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'consent-reopen';
	button.textContent = translation.preferencesModal.title;
	button.addEventListener('click', () => cookieConsent?.showPreferences());
	document.body.appendChild(button);
};

/**
 * Loads the banner library and shows the banner (when there is no choice yet) and the settings button.
 */
export const initConsent = async (): Promise<void> => {
	if (started) {
		return;
	}
	started = true;

	const [library] = await Promise.all([import("vanilla-cookieconsent"), import("vanilla-cookieconsent/dist/cookieconsent.css")]);
	cookieConsent = library;

	const lang = (Cookie.get('lang') ?? 'hu') === 'hu' ? 'hu' : 'en';
	const translations = {hu: translationsFor('hu'), en: translationsFor('en')};

	await library.run({
		cookie: {name: COOKIE_NAME, expiresAfterDays: COOKIE_DAYS, sameSite: 'Lax'},
		guiOptions: {
			// Accepting and rejecting look the same: the refusal is as easy as the consent
			consentModal: {layout: 'box inline', position: 'bottom center', equalWeightButtons: true, flipButtons: false},
			preferencesModal: {layout: 'box', equalWeightButtons: true, flipButtons: false},
		},
		categories: {
			necessary: {enabled: true, readOnly: true},
			analytics: {},
			marketing: {},
			external: {},
		},
		language: {default: lang, translations},
		onFirstConsent: ({cookie}) => {
			choiceChanged(cookie.categories, false);
			addReopenButton(translations[lang]);
		},
		onConsent: ({cookie}) => sync(cookie.categories),
		onChange: ({cookie}) => choiceChanged(cookie.categories, true),
	});

	// The settings button is there once there is a choice (before it the banner is on the screen)
	if (library.validConsent()) {
		sync(library.getUserPreferences().acceptedCategories);
		addReopenButton(translations[lang]);
	}
};

/**
 * Allows the external content (maps, videos) from the place where it is blocked.
 */
export const allowExternal = (): void => {
	if (!cookieConsent) {
		void initConsent().then(() => cookieConsent?.showPreferences());
		return;
	}
	const current = cookieConsent.getUserPreferences().acceptedCategories;
	cookieConsent.acceptCategory([...new Set([...current, 'necessary', 'external'])]);
};

export const openPreferences = (): void => {
	if (!cookieConsent) {
		void initConsent().then(() => cookieConsent?.showPreferences());
		return;
	}
	cookieConsent.showPreferences();
};

/**
 * Resolves when the Google Maps API is loaded (it starts after the consent for the external content).
 */
export const whenMapsReady = (): Promise<void> => new Promise((resolve) => {
	if ((window as any).google?.maps) {
		resolve();
		return;
	}
	document.addEventListener('albalux-maps-loaded', () => resolve(), {once: true});
});

export type EmbedKind = 'map' | 'video' | 'content';

/**
 * The text of the place of a blocked embed.
 */
export const placeholderTexts = (kind: EmbedKind): { text: string, allow: string, settings: string } => {
	const hu = (Cookie.get('lang') ?? 'hu') === 'hu';
	const what = {
		map: hu ? 'a Google Térkép' : 'the Google map',
		video: hu ? 'a videó' : 'the video',
		content: hu ? 'a külső tartalom' : 'the external content',
	}[kind];

	return hu
		? {text: `Ez a tartalom külső szolgáltatótól (Google, YouTube) érkezik. A megtekintéséhez engedélyezd a külső tartalmakat: ${what} csak ezután töltődik be.`, allow: 'Külső tartalmak engedélyezése', settings: 'Süti beállítások'}
		: {text: `This content comes from an external provider (Google, YouTube). To see it, allow the external content: ${what} is loaded only after that.`, allow: 'Allow external content', settings: 'Cookie settings'};
};
